import type { ArtifactDomainShare, ArtifactRecipient } from "@/types/sharedArtifact.ts";

/**
 * Artifact types that can be shared. Mirrors the server's allowlist — the
 * server refuses anything else with a 400, so this only decides whether the
 * control is offered at all.
 */
const SHAREABLE_TYPES = new Set(["document", "applet", "file"]);

export const isShareableArtifactType = (artifactType: string): boolean =>
  SHAREABLE_TYPES.has(artifactType);

/** How the share sheet names what is being shared: its title, or its kind. */
export const shareSubjectLabel = (artifactType: string, title?: string | null): string => {
  const trimmed = title?.trim();
  if (trimmed) return trimmed;
  if (artifactType === "applet") return "this applet";
  if (artifactType === "file") return "this file";
  return "this document";
};

/**
 * Whether to offer sharing for this artifact on this surface.
 *
 * The share endpoints resolve the artifact inside the caller's own account, so
 * a non-owner gets a 404 and nothing else. Each clause rules out a different
 * way someone reaches an artifact that isn't theirs:
 *
 * - a shareable type, mirroring the server allowlist;
 * - `isOwner !== false` — the by-id endpoint stamps `false` when the read
 *   resolved through a share grant. Absent is not "stranger": the field is
 *   optional and the thread feed and Library payloads simply never carry it,
 *   so testing for `=== true` would drop the button on those surfaces;
 * - not a shared thread, which is the one place a non-owner sees an artifact
 *   whose payload has no `isOwner` to test at all;
 * - `isLatestVersion === true`. A share is anchored to the lineage, so sharing a
 *   prior version hands out the *newest* content while the user is looking at an
 *   older one. Prior versions stay openable from the thread; they just cannot be
 *   shared from.
 *
 * That last one tests strictly, unlike `isOwner`. The field is absent on an
 * older server, and FE and BE deploy independently — hiding the button for the
 * rollover window is the safe direction to fail, where offering a misleading one
 * is not.
 *
 * Takes the route as an argument rather than reading it itself: web's
 * `CanvasArtifactModal` decides this after four early returns, so a hook here
 * could not be called unconditionally.
 */
export const canShareArtifact = (
  artifact: { artifactType: string; isOwner?: boolean; isLatestVersion?: boolean },
  { isSharedThread }: { isSharedThread: boolean },
): boolean =>
  isShareableArtifactType(artifact.artifactType) &&
  artifact.isOwner !== false &&
  artifact.isLatestVersion === true &&
  !isSharedThread;

/** The part of an address after the `@`, for matching it against a domain share. */
const domainOf = (email: string): string => email.slice(email.lastIndexOf("@") + 1).toLowerCase();

/**
 * A typed domain, tidied for the confirmation's wording. Only tidied: the
 * server's `normalizeDomain` decides what a domain is.
 */
export const tidyDomainInput = (value: string): string =>
  value.trim().replace(/^@/, "").toLowerCase();

/**
 * What removing one person tells the owner, naming the domain or link that still
 * reaches them. Speaks of the domain, never the person's account.
 */
export const recipientRemovedMessage = ({
  email,
  domains,
  stillReachableByLink,
}: {
  email: string;
  domains: ArtifactDomainShare[];
  stillReachableByLink: boolean;
}): string => {
  const covering = domains.find((share) => share.domain === domainOf(email));
  if (covering) return `Removed — but it's still shared with everyone at ${covering.domain}`;
  if (stillReachableByLink) return "Removed — but they can still open the link";
  return "Access removed";
};

/**
 * What removing a domain tells the owner, naming whoever it still reaches.
 */
export const domainRemovedMessage = ({
  domain,
  recipients,
  stillReachableByLink,
}: {
  domain: string;
  recipients: ArtifactRecipient[];
  stillReachableByLink: boolean;
}): string => {
  if (recipients.some((recipient) => domainOf(recipient.email) === domain)) {
    return `Stopped sharing with ${domain} — people you added by name keep access`;
  }
  if (stillReachableByLink) {
    return `Stopped sharing with ${domain} — but anyone with the link can still open it`;
  }
  return `Stopped sharing with ${domain}`;
};
